import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function PrivacyPolicyPage() {
  const { language } = useLanguage();
  const isLV = language === 'lv';

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {isLV ? 'Atpakaļ uz sākumu' : 'Back to home'}
        </Link>

        <div className="bg-white rounded-2xl border border-[#E5E4E0] p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#D97736] flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#121212]">
                {isLV ? 'Privātuma politika' : 'Privacy Policy'}
              </h1>
              <p className="text-sm text-[#5C5C5C]">
                {isLV ? 'Spēkā no 2026. gada' : 'Effective 2026'}
              </p>
            </div>
          </div>

          <article className="prose prose-sm max-w-none text-[#2B2B2B] leading-relaxed space-y-5">
            {isLV ? (
              <>
                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">1. Vispārīga informācija</h2>
                  <p>
                    Šī privātuma politika apraksta, kā SIA “Gatis” (turpmāk — “Pārzinis”, “KoLV”, “mēs”)
                    apstrādā personas datus, kad izmantojat tīmekļa vietni <strong>kolv.lv</strong>.
                    Mēs ievērojam ES Vispārīgo datu aizsardzības regulu (VDAR, Regula 2016/679) un
                    Latvijas Republikas normatīvos aktus.
                  </p>
                  <div className="text-xs text-[#5C5C5C] bg-[#FAFAFA] border border-[#E5E4E0] rounded-lg p-3 mt-2 space-y-0.5">
                    <p><strong>Pārzinis:</strong> SIA “Gatis”</p>
                    <p><strong>Reģistrācijas numurs:</strong> 50203491851 (reģistrēta Komercreģistrā 19.06.2023)</p>
                    <p><strong>Juridiskā adrese:</strong> Toma iela 7 - 44, Liepāja, LV-3401</p>
                    <p><strong>E-pasts saziņai:</strong> kolatvietim@gmail.com</p>
                    <p><strong>Tīmekļa vietne:</strong> kolv.lv</p>
                  </div>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">2. Kādus datus mēs apstrādājam</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Konta dati:</strong> vārds, e-pasts, tālrunis (neobligāts), parole (šifrēta), reģions.</li>
                    <li><strong>Pārdevēju dati:</strong> juridiskais nosaukums, reģistrācijas numurs, PVN numurs, juridiskā adrese, bankas rekvizīti, veikala logotips, apraksts.</li>
                    <li><strong>Sludinājumu dati:</strong> nosaukums, apraksts, bildes, cena, kategorija, atrašanās vieta.</li>
                    <li><strong>Darījumu dati:</strong> pasūtījumi, maksājumi (caur Stripe), maciņa bilance, komisijas aprēķini.</li>
                    <li><strong>Saziņas dati:</strong> ziņas starp pircēju un pārdevēju, atsauksmes, paziņojumi.</li>
                    <li><strong>Tehniskie dati:</strong> IP adrese, pārlūka versija, sesijas sīkdatnes, neveiksmīgu pieslēgumu ieraksti (drošībai).</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">3. Apstrādes tiesiskais pamats</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Līguma izpilde</strong> (VDAR 6.1.b) — konta uzturēšana, pasūtījumu apstrāde, maksājumu veikšana.</li>
                    <li><strong>Juridisks pienākums</strong> (VDAR 6.1.c) — grāmatvedības un nodokļu dati, patērētāju tiesību ievērošana.</li>
                    <li><strong>Leģitīmas intereses</strong> (VDAR 6.1.f) — krāpšanas novēršana, drošība, pakalpojuma uzlabošana.</li>
                    <li><strong>Piekrišana</strong> (VDAR 6.1.a) — mārketinga ziņojumi, neobligātās sīkdatnes (analītika).</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">4. Cik ilgi mēs glabājam datus</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Konta dati — kamēr konts ir aktīvs + 1 gads pēc dzēšanas (strīdu risināšanai).</li>
                    <li>Darījumu dati — 5 gadi (grāmatvedības likuma prasības).</li>
                    <li>Saziņas arhīvs — 2 gadi.</li>
                    <li>Tehniskie žurnāli — 90 dienas.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">5. Kam mēs nododam datus</h2>
                  <p>
                    Mēs NEPĀRDODAM Tavus datus. Nododam tikai šādiem apstrādātājiem, ar kuriem mums ir
                    datu apstrādes līgumi:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Stripe Inc.</strong> (ASV) — maksājumu apstrāde un maciņa papildināšana.</li>
                    <li><strong>Resend</strong> (ES) — transakciju e-pastu sūtīšana (apstiprinājumi, paziņojumi).</li>
                    <li><strong>Mākoņa infrastruktūras piegādātāji</strong> — datu glabāšana un pakalpojuma hostings ES/EEZ.</li>
                    <li>Valsts institūcijas — tikai ja to pieprasa likums (piem., VID, tiesa).</li>
                  </ul>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">6. Tavas tiesības</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Piekļūt saviem datiem un saņemt to kopiju.</li>
                    <li>Labot neprecīzus datus.</li>
                    <li>Dzēst kontu un datus (“tiesības tikt aizmirstam”) — ja vien nav likumisks pienākums tos saglabāt.</li>
                    <li>Ierobežot apstrādi vai iebilst pret to.</li>
                    <li>Datu pārnesamība — saņemt datus mašīnlasāmā formātā.</li>
                    <li>Atsaukt piekrišanu jebkurā laikā.</li>
                    <li>Iesniegt sūdzību Datu valsts inspekcijā (<a href="https://www.dvi.gov.lv" className="text-[#D97736] underline">dvi.gov.lv</a>).</li>
                  </ul>
                  <p>Rakstiet uz <strong>kolatvietim@gmail.com</strong> — atbildēsim 30 dienu laikā.</p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">7. Drošība</h2>
                  <p>
                    Paroles tiek glabātas šifrētā (bcrypt) veidā. Visa saziņa notiek pa HTTPS (TLS).
                    Sesijas sīkdatnes ir <code>HttpOnly</code> un <code>Secure</code>.
                    Maksājumu kartes dati nekad netiek glabāti mūsu serveros — tos apstrādā tikai Stripe.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">8. Izmaiņas politikā</h2>
                  <p>
                    Par būtiskām izmaiņām informēsim pa e-pastu vai ar paziņojumu platformā vismaz 30 dienas iepriekš.
                  </p>
                </section>

                <section>
                  <h2 className="font-outfit text-lg font-semibold text-[#121212]">9. Kontakti</h2>
                  <p>
                    SIA “Gatis” (reģ.nr. 50203491851)<br />
                    Toma iela 7 - 44, Liepāja, LV-3401<br />
                    E-pasts: <strong>kolatvietim@gmail.com</strong><br />
                    Tīmekļa vietne: <a href="https://kolv.lv" className="text-[#D97736] underline">kolv.lv</a>
                  </p>
                </section>
              </>
            ) : (
              <p className="text-[#5C5C5C]">
                English version available on request. Please see the Latvian version above for full details.
              </p>
            )}
          </article>
        </div>
      </div>
    </div>
  );
}
